import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { selectedAuditRows, processAuditSelection } from './auditSelection';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import Button from '../../components/ui/Button';
import { useModal } from '../../components/ui/ModalProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { AUDIT_STATUSES, auditRequest, destinationHref, isAuditActive, loadAuditAccounts, loadAuditHistory, loadAuditResults } from '../../services/pinterestAuditService';

const formatTime = value => value ? new Date(value).toLocaleString() : '—';
const pluralLabels = { REDIRECT: 'Redirects', SERVER_ERROR: 'Server Errors', TIMEOUT: 'Timeouts' };

// Remount the entire view on a user/profile change: stale responses cannot enter
// the new profile, even for the render before effect cleanup runs.
export default function PinterestAudit() {
  const { user } = useAuth();
  const { activeProfileId, loading } = useLanguageProfile();
  return <SidebarProvider><div className="min-h-screen bg-background"><Sidebar />
    <main className="main-content"><div className="max-w-[1600px] mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900">Pinterest Audit</h1>
      <p className="text-gray-600 mt-2 mb-6">Analyze your Pinterest account and detect broken or problematic destination links.</p>
      {loading || !user?.id || !activeProfileId ? <p>Select a Gymplix profile to load its Pinterest accounts.</p>
        : <ProfileAudit key={`${user.id}:${activeProfileId}`} userId={user.id} profileId={activeProfileId} />}
    </div></main>
  </div></SidebarProvider>;
}

function ProfileAudit({ userId, profileId }) {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    loadAuditAccounts(userId, profileId, controller.signal).then(setAccounts).catch(e => {
      if (!controller.signal.aborted) setError(e.message);
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [userId, profileId]);
  return <>
    <label htmlFor="audit-account" className="block text-sm font-medium mb-2">Pinterest Account:</label>
    <select id="audit-account" className="border border-gray-300 rounded-md bg-white p-2 min-w-64 max-w-full" value={accountId}
      disabled={loading} onChange={e => setAccountId(e.target.value)}>
      <option value="">{loading ? 'Loading accounts…' : 'Select Pinterest Account'}</option>
      {accounts.map(account => <option key={account.id} value={account.id}>{account.username || 'Pinterest Account'}</option>)}
    </select>
    {error && <p role="alert" className="text-red-700 mt-3">{error}</p>}
    {!loading && !error && !accounts.length && <p className="mt-3 text-gray-600">No Pinterest accounts belong to this profile. <Link className="text-primary underline" to="/pin/accounts">Manage Pinterest accounts</Link></p>}
    {accountId ? <AccountAudit key={accountId} userId={userId} profileId={profileId} accountId={accountId} />
      : <div className="mt-4"><Button disabled>Run Audit</Button></div>}
  </>;
}

function AccountAudit({ userId, profileId, accountId }) {
  const { confirm } = useModal();
  const [rowActions, setRowActions] = useState({});
  const [rowRevision, setRowRevision] = useState(0);
  const pendingRows = useRef(new Set());
  const [selection, setSelection] = useState({ view: '', ids: new Set() });
  const selectionRef = useRef(selection);
  const selectionGeneration = useRef(0);
  const [selectionBusy, setSelectionBusy] = useState(false);
  const selectionBusyRef = useRef(false);
  const [selectionProgress, setSelectionProgress] = useState(null);
  const visibleRows = useRef([]);
  const currentRunId = useRef(null);
  const currentView = useRef('');
  const loadedView = useRef('');
  const [history, setHistory] = useState([]);
  const [run, setRun] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [results, setResults] = useState({ rows: [], count: 0, view: '' });
  const [loadingResults, setLoadingResults] = useState(false);
  const controller = useRef(null);
  const alive = useRef(true);
  const busyRef = useRef(false);
  currentRunId.current = run?.id;
  currentView.current = JSON.stringify([run?.id, filter, search, page]);
  selectionRef.current = selection;
  visibleRows.current = results.view === currentView.current ? results.rows : [];
  const selectedRows = selectedAuditRows(visibleRows.current, selection, currentView.current);
  const updateSelection = value => { selectionRef.current = value; setSelection(value); };
  const clearSelection = () => {
    selectionGeneration.current++;
    updateSelection({ view: '', ids: new Set() });
    setSelectionProgress(null);
  };
  const toggleSelected = (id, checked) => {
    const ids = new Set(selectedRows.map(row => row.id));
    if (checked) ids.add(id); else ids.delete(id);
    updateSelection({ view: currentView.current, ids });
  };

  useEffect(() => {
    alive.current = true;
    const abort = new AbortController();
    loadAuditHistory(userId, profileId, accountId, abort.signal).then(data => {
      if (abort.signal.aborted) return;
      setHistory(data); setRun(data[0] || null);
    }).catch(e => { if (!abort.signal.aborted) setError(e.message); })
      .finally(() => { if (!abort.signal.aborted) setReady(true); });
    return () => { alive.current = false; abort.abort(); controller.current?.abort(); };
  }, [userId, profileId, accountId]);

  useEffect(() => {
    const view = currentView.current;
    const changedView = loadedView.current !== view;
    loadedView.current = currentView.current;
    if (changedView) setResults({ rows: [], count: 0, view: '' });
    if (!run?.id) return;
    const abort = new AbortController();
    setLoadingResults(changedView);
    const timer = setTimeout(() => {
      loadAuditResults(run.id, accountId, filter, search, page, abort.signal).then(data => {
        if (!abort.signal.aborted) setResults({ ...data, view });
      }).catch(e => { if (!abort.signal.aborted) setError(e.message); })
        .finally(() => { if (!abort.signal.aborted) setLoadingResults(false); });
    }, 250);
    return () => { clearTimeout(timer); abort.abort(); };
  }, [run?.id, run?.checked_count, run?.total_pins, accountId, filter, search, page, rowRevision]);

  useEffect(() => { clearSelection(); }, [run?.id, filter, search, page]);

  const remember = value => {
    setRun(value);
    setHistory(previous => [value, ...previous.filter(item => item.id !== value.id)].slice(0, 20));
  };
  const execute = async () => {
    if (busyRef.current || selectionBusyRef.current) return;
    clearSelection();
    busyRef.current = true; setBusy(true); setError(''); setNotice(''); setFilter('ALL'); setSearch(''); setPage(0);
    const abort = new AbortController();
    controller.current = abort;
    try {
      // start is idempotent while an account has an active run, including other tabs.
      let current = await auditRequest('start', profileId, accountId, null, abort.signal);
      if (abort.signal.aborted) return;
      remember(current);
      while (isAuditActive(current) && !abort.signal.aborted) {
        const waitUntil = Math.max(Date.parse(current.next_attempt_at) || 0, Date.parse(current.lease_until) || 0);
        const delay = Math.min(30000, Math.max(1000, waitUntil - Date.now()));
        await new Promise(resolve => {
          const done = () => { clearTimeout(timer); abort.signal.removeEventListener('abort', done); resolve(); };
          const timer = setTimeout(done, delay);
          abort.signal.addEventListener('abort', done, { once: true });
        });
        if (abort.signal.aborted) return;
        if (waitUntil > Date.now()) continue;
        current = await auditRequest('step', profileId, accountId, current.id, abort.signal);
        if (!abort.signal.aborted) remember(current);
      }
    } catch (e) {
      if (!abort.signal.aborted) setError(e.message);
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };

  const actOnRow = async (row, action) => {
    if (pendingRows.current.has(row.id)) return;
    const runId = run.id;
    const view = currentView.current;
    pendingRows.current.add(row.id);
    setRowActions(previous => ({ ...previous, [row.id]: { action, loading: true } }));
    try {
      if (!alive.current || currentRunId.current !== runId) return;
      const data = await auditRequest(action, profileId, accountId, runId, undefined, {
        resultId: row.id, ...(action === 'delete' ? { confirmed: true } : {}),
      });
      if (!alive.current) return;
      setHistory(previous => previous.map(item => item.id === data.run.id ? data.run : item));
      if (currentRunId.current !== runId) return;
      setRun(data.run);
      if (currentView.current === view) {
        const matches = data.row && (filter === 'ALL' || filter === data.row.health_status ||
          (filter === 'PROBLEMS' && ['BROKEN', 'SERVER_ERROR', 'TIMEOUT', 'CHECK_ERROR', 'WRONG_DOMAIN', 'NO_DESTINATION'].includes(data.row.health_status)));
        setResults(previous => ({
          ...previous,
          rows: matches ? previous.rows.map(item => item.id === row.id ? data.row : item) : previous.rows.filter(item => item.id !== row.id),
          count: matches ? previous.count : Math.max(0, previous.count - 1),
        }));
      }
      // Refresh the current page silently to refill it and honor existing filters.
      const remaining = new Set(selectionRef.current.ids);
      remaining.delete(row.id);
      updateSelection({ view: selectionRef.current.view, ids: remaining });
      setRowRevision(value => value + 1);
      return true;
    } catch (e) {
      if (alive.current) setRowActions(previous => ({ ...previous, [row.id]: { action, error: e.message } }));
      return false;
    } finally {
      pendingRows.current.delete(row.id);
      if (alive.current) setRowActions(previous => ({ ...previous, [row.id]: { ...previous[row.id], loading: false } }));
    }
  };

  const actOnSelected = async action => {
    if (selectionBusyRef.current || busyRef.current) return;
    const view = currentView.current;
    const generation = selectionGeneration.current;
    const rows = selectedAuditRows(visibleRows.current, selectionRef.current, view, action);
    if (!rows.length) return;
    selectionBusyRef.current = true;
    setSelectionBusy(true);
    try {
      await processAuditSelection({
        action, rows,
        confirm: message => confirm(message, { confirmText: 'Delete', cancelText: 'Cancel' }),
        isCurrent: () => alive.current && currentView.current === view && selectionGeneration.current === generation,
        isSelected: id => selectedAuditRows(visibleRows.current, selectionRef.current, view, action).some(row => row.id === id),
        perform: actOnRow,
        onProgress: setSelectionProgress,
      });
    } finally {
      selectionBusyRef.current = false;
      if (alive.current) setSelectionBusy(false);
    }
  };

  const copy = async value => {
    try { await navigator.clipboard.writeText(value); setNotice('Destination URL copied.'); }
    catch { setNotice('Could not copy the URL. Select and copy it from the Destination column.'); }
  };
  const active = isAuditActive(run);
  const lastCompleted = history.find(item => item.completed_at);
  return <div className="mt-4 space-y-5">
    <div className="flex flex-wrap items-center gap-4">
      <Button onClick={execute} disabled={!ready || busy || selectionBusy} loading={busy}>{busy ? 'Audit Running…' : active ? 'Resume Audit' : run ? 'Run Again' : 'Run Audit'}</Button>
      {lastCompleted && <p className="text-sm text-gray-600">Last audit: {formatTime(lastCompleted.completed_at)}</p>}
      {history.length > 1 && <label className="text-sm">Audit history: <select className="border rounded p-2 bg-white" disabled={busy} value={run?.id || ''} onChange={e => {
        clearSelection(); setRun(history.find(item => item.id === e.target.value)); setPage(0); setError('');
      }}>{history.map(item => <option value={item.id} key={item.id}>{formatTime(item.started_at)} — {item.status}</option>)}</select></label>}
    </div>
    {error && <p role="alert" className="rounded-lg bg-red-50 text-red-800 p-4">{error}</p>}
    {run?.error_message && <p role="alert" className="rounded-lg bg-amber-50 text-amber-900 p-4">{run.error_message}{/Reconnect|permissions/i.test(run.error_message) && <> <Link to="/pin/accounts" className="underline">Manage Pinterest accounts</Link></>}</p>}
    {notice && <p role="status" className="text-sm text-gray-600">{notice}</p>}
    {run && <>
      <div className="rounded-xl border bg-white p-4" role="status" aria-live="polite">
        {active ? <>
          <p className="font-medium">{run.status === 'fetching' ? 'Fetching Pinterest Pins…' : 'Checking destination URLs…'}</p>
          <p className="text-sm text-gray-600 mt-1">{run.status === 'fetching' ? `${run.total_pins} Pins found…` : `${run.checked_count} / ${run.total_pins} checked…`}</p>
          {run.retry_count > 0 && <p className="text-sm mt-1">Retry {run.retry_count} / 5, no earlier than {formatTime(run.next_attempt_at)}.</p>}
          <p className="text-xs text-gray-500 mt-2">{busy ? 'Keep this page open to continue. You can resume this audit after leaving.' : 'This audit is paused. Resume to continue from its saved progress.'}</p>
        </> : <p className="font-medium">{run.status === 'completed' ? (run.total_pins ? 'Audit complete' : 'Audit complete — this account has no accessible owned Pins.') : run.status === 'partial' ? 'Partial audit — fetched Pins were checked, but Pinterest retrieval was incomplete.' : 'Audit could not be completed.'}</p>}
        <p className="text-xs text-gray-500 mt-2">{run.expected_domains?.length ? `Expected WordPress domains: ${run.expected_domains.join(', ')}. www and non-www are treated as the same domain.` : 'No WordPress domain is configured for this profile. Wrong Domain checks are skipped.'}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-2">
        <div className="border rounded-xl bg-white p-2 min-w-0"><p className="text-xs text-gray-600">Total Pins</p><p className="text-xl font-semibold mt-1">{run.total_pins}</p></div>
        {AUDIT_STATUSES.map(([status, label, count, color]) => <div key={status} className={`border rounded-xl p-2 min-w-0 ${color}`}><p className="text-xs">{pluralLabels[status] || label}</p><p className="text-xl font-semibold mt-1">{run[count]}</p></div>)}
      </div>
      <div className="flex flex-wrap lg:flex-nowrap items-center gap-2">
        <label className="sr-only" htmlFor="audit-filter">Filter results</label>
        <select id="audit-filter" value={filter} onChange={e => { clearSelection(); setFilter(e.target.value); setPage(0); }} className="border rounded-md p-2 bg-white">
          <option value="ALL">All</option><option value="PROBLEMS">Problems</option>
          {AUDIT_STATUSES.map(([status, label]) => <option key={status} value={status}>{pluralLabels[status] || label}</option>)}
        </select>
        <label className="sr-only" htmlFor="audit-search">Search Pin title, Pin ID or destination URL</label>
        <input id="audit-search" value={search} onChange={e => { clearSelection(); setSearch(e.target.value); setPage(0); }} placeholder="Search Pin title, Pin ID or destination URL" className="border rounded-md p-2 flex-1 min-w-0" />
        <div className="flex shrink-0 gap-2 lg:ml-auto">
          <Button size="sm" variant="outline" disabled={selectionBusy || busy || loadingResults || !selectedAuditRows(visibleRows.current, selection, currentView.current, 'recheck').length} onClick={() => actOnSelected('recheck')}>Recheck Selected</Button>
          <Button size="sm" variant="outline" disabled={selectionBusy || busy || loadingResults || !selectedAuditRows(visibleRows.current, selection, currentView.current, 'fix_redirect').length} onClick={() => actOnSelected('fix_redirect')}>Fix Redirects</Button>
          <Button size="sm" variant="destructive" disabled={selectionBusy || busy || loadingResults || !selectedAuditRows(visibleRows.current, selection, currentView.current, 'delete').length} onClick={() => actOnSelected('delete')}>Delete Selected</Button>
        </div>
      </div>
      {(selectedRows.length > 0 || selectionProgress || selectionBusy) && <p role="status" aria-live="polite" className="text-sm text-gray-600">
        {selectedRows.length} selected on this page.
        {selectionProgress && ` ${selectionProgress.action === 'delete' ? 'Delete' : selectionProgress.action === 'fix_redirect' ? 'Fix Redirects' : 'Recheck'}: ${selectionProgress.done} / ${selectionProgress.total} processed; ${selectionProgress.failed} failed; ${selectionProgress.skipped} no longer selected or eligible.`}
        {selectionBusy && ' Processing…'}
      </p>}
      <div className="overflow-x-auto border rounded-xl bg-white">
        <table className="w-full text-sm text-left"><thead className="bg-gray-50 text-gray-600"><tr><th scope="col" className="px-3 py-3"><input type="checkbox" aria-label="Select all Pins shown on this page" disabled={selectionBusy || loadingResults || !visibleRows.current.length}
          checked={visibleRows.current.length > 0 && selectedRows.length === visibleRows.current.length}
          ref={element => { if (element) element.indeterminate = selectedRows.length > 0 && selectedRows.length < visibleRows.current.length; }}
          onChange={e => updateSelection({ view: currentView.current, ids: new Set(e.target.checked ? visibleRows.current.map(row => row.id) : []) })} /></th>{['Pin', 'Pin ID', 'Destination', 'Final URL', 'HTTP', 'Status', 'Last Checked', 'Actions'].map(label => <th scope="col" key={label} className="px-4 py-3 whitespace-nowrap">{label}</th>)}</tr></thead>
          <tbody>{results.rows.map(row => {
            const status = AUDIT_STATUSES.find(([value]) => value === row.health_status);
            const href = destinationHref(row.destination_url);
            return <tr key={row.id} className="border-t align-top">
              <td className="px-3 py-3"><input type="checkbox" aria-label={`Select Pin ${row.pin_id}`} disabled={selectionBusy || loadingResults || results.view !== currentView.current} checked={selectedRows.some(item => item.id === row.id)} onChange={e => toggleSelected(row.id, e.target.checked)} /></td>
              <td className="px-4 py-3 min-w-40 max-w-64 break-words">{row.pin_title || 'Untitled Pin'}</td>
              <td className="px-4 py-3 font-mono">{row.pin_id}</td>
              <td className="px-4 py-3 max-w-64 break-all">{row.destination_url || '—'}</td>
              <td className="px-4 py-3 max-w-64 break-all">{row.final_url || '—'}</td>
              <td className="px-4 py-3">{row.http_status || '—'}</td>
              <td className="px-4 py-3 min-w-40"><span className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${status?.[3] || 'bg-gray-100 text-gray-600'}`}>{status?.[1] || 'Pending'}</span>{row.error_message && <p className="text-xs text-gray-600 mt-2 max-w-64">{row.error_message}</p>}</td>
              <td className="px-4 py-3 whitespace-nowrap">{formatTime(row.checked_at)}</td>
              <td className="px-4 py-3"><div className="flex flex-col items-start gap-2 whitespace-nowrap">
                {rowActions[row.id]?.error && <p role="alert" className="text-xs text-red-700 whitespace-normal max-w-64">{rowActions[row.id].error}</p>}
                <a href={`https://www.pinterest.com/pin/${encodeURIComponent(row.pin_id)}/`} target="_blank" rel="noopener noreferrer" className="text-primary underline">Open Pin</a>
                {href ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">Open Destination</a> : <span className="text-gray-400">Open Destination</span>}
                <button type="button" disabled={!row.destination_url} onClick={() => copy(row.destination_url)} className="text-primary underline disabled:text-gray-400 disabled:no-underline">Copy URL</button>
              </div></td>
            </tr>;
          })}
          {!results.rows.length && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">{loadingResults ? 'Loading results…' : 'No results to display.'}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm"><span>{results.count} matching Pins · Page {page + 1} of {Math.max(1, Math.ceil(results.count / 50))}</span><div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={!page || loadingResults} onClick={() => { clearSelection(); setPage(value => value - 1); }}>Previous</Button>
        <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= results.count || loadingResults} onClick={() => { clearSelection(); setPage(value => value + 1); }}>Next</Button>
      </div></div>
    </>}
  </div>;
}
